import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FaCog } from 'react-icons/fa';
import './ContactPage.css';
import Footer from './Footer';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

const CLOUD_FUNCTION_CONTACT_URL = 'https://submitcontactform-vmjoeijf4a-uc.a.run.app';

const MAX_NAME_CHARS = 100;
const MAX_EMAIL_CHARS = 100;
const MAX_TOPIC_CHARS = 150;
const MAX_ISSUE_CHARS = 1000;

const ContactPage = () => {
    useEffect(() => {
        document.title = "Wunner - Contact Us";
    }, []);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        topic: '',
        issue: ''
    });

    const [errors, setErrors] = useState({});

    const [submissionMessage, setSubmissionMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { executeRecaptcha } = useGoogleReCaptcha();

    const handleChange = (e) => {
        const { name, value } = e.target;
        let newValue = value;

        switch (name) {
            case 'name':
                newValue = value.slice(0, MAX_NAME_CHARS);
                break;
            case 'email':
                newValue = value.slice(0, MAX_EMAIL_CHARS);
                break;
            case 'topic':
                newValue = value.slice(0, MAX_TOPIC_CHARS);
                break;
            case 'issue':
                newValue = value.slice(0, MAX_ISSUE_CHARS);
                break;
            default:
                break;
        }

        setFormData(prev => ({ ...prev, [name]: newValue }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        let newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        } else if (formData.name.length > MAX_NAME_CHARS) {
            newErrors.name = `Name must be less than ${MAX_NAME_CHARS + 1} characters`;
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        } else if (formData.email.length > MAX_EMAIL_CHARS) {
            newErrors.email = `Email must be less than ${MAX_EMAIL_CHARS + 1} characters`;
        }

        if (!formData.topic.trim()) {
            newErrors.topic = 'Topic is required';
        } else if (formData.topic.length > MAX_TOPIC_CHARS) {
            newErrors.topic = `Topic must be less than ${MAX_TOPIC_CHARS + 1} characters`;
        }

        if (!formData.issue.trim()) {
            newErrors.issue = 'Issue description is required';
        } else if (formData.issue.length > MAX_ISSUE_CHARS) {
            newErrors.issue = `Issue must be less than ${MAX_ISSUE_CHARS + 1} characters`;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();

        setSubmissionMessage('');
        setIsSubmitting(true);

        if (!validateForm()) {
            setIsSubmitting(false);
            setSubmissionMessage('Please correct the errors in the form.');
            return;
        }

        try {
            if (!executeRecaptcha) {
                console.log('reCAPTCHA hook not available yet');
                setIsSubmitting(false);
                setSubmissionMessage('reCAPTCHA not loaded. Please try again.');
                return;
            }
            const recaptchaToken = await executeRecaptcha('contact_form_submit');

            const dataToSend = {
                name: formData.name,
                email: formData.email,
                subject: formData.topic,
                issue: formData.issue,
                recaptchaToken: recaptchaToken
            };

            const response = await fetch(CLOUD_FUNCTION_CONTACT_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(dataToSend),
            });

            if (response.ok) {
                const data = await response.json();
                setSubmissionMessage(data.message || 'Message sent successfully!');
                setFormData({ name: '', email: '', topic: '', issue: '' });
            } else {
                const errorData = await response.json();
                setSubmissionMessage(errorData.message || 'Failed to send message.');
            }
        } catch (error) {
            console.error('Submission error:', error);
            setSubmissionMessage('An error occurred during submission. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    }, [formData, executeRecaptcha, validateForm]);

    return (
        <div className="contact-container">
            <div>
                <Link to="/">
                    <img src="/logo.png" alt="Game Logo" className="contact-container-logo" />
                </Link>
            </div>

            <Link to="/settings" className="back-to-settings-button">
                <span className="back-settings-text">Settings</span>
                <FaCog className="back-button-icon" />
            </Link>
            <div className="contact-form-container">
                <h2 className="contact-title">Contact Us</h2>
                {submissionMessage && (
                    <p className={`submission-message ${submissionMessage.includes('successfully') ? 'success' : 'error'}`}>
                        {submissionMessage}
                    </p>
                )}
                <form className="contact-form" onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="name">Name:</label>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            maxLength={MAX_NAME_CHARS}
                            className={errors.name ? 'input-error' : ''}
                        />
                        {errors.name && <p className="error-text">{errors.name}</p>}
                    </div>

                    <div className="form-group">
                        <label htmlFor="email">Email:</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            maxLength={MAX_EMAIL_CHARS}
                            required
                            className={errors.email ? 'input-error' : ''}
                        />
                        {errors.email && <p className="error-text">{errors.email}</p>}
                    </div>

                    <div className="form-group">
                        <label htmlFor="topic">Topic:</label>
                        <input
                            type="text"
                            id="topic"
                            name="topic"
                            value={formData.topic}
                            onChange={handleChange}
                            maxLength={MAX_TOPIC_CHARS}
                            required
                            className={errors.topic ? 'input-error' : ''}
                        />
                        {errors.topic && <p className="error-text">{errors.topic}</p>}
                    </div>

                    <div className="form-group">
                        <label htmlFor="issue">Issue:</label>
                        <textarea
                            id="issue"
                            name="issue"
                            value={formData.issue}
                            onChange={handleChange}
                            maxLength={MAX_ISSUE_CHARS}
                            required
                            rows="5"
                            className={errors.issue ? 'input-error' : ''}
                        ></textarea>
                        {errors.issue && <p className="error-text">{errors.issue}</p>}
                    </div>

                    <button type="submit" className="submit-button" disabled={isSubmitting}>
                        {isSubmitting ? 'Sending...' : 'Submit'}
                    </button>

                    <p className="recaptcha-attribution">
                        This site is protected by reCAPTCHA and the Google&nbsp;
                        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and&nbsp;
                        <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> apply.
                    </p>

                </form>
            </div>
            <Footer />
        </div>
    );
};

export default ContactPage;